import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Grid, Card, CardContent, Box, TextField, FormGroup, FormControlLabel,
    Checkbox, Button, Stack, List, ListItem, ListItemButton, ListItemText, IconButton, TableContainer,
    Paper, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent,
    DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { api, getContentText, getAuthorName } from '../api';

const KeywordsPage = ({ state, refreshData }) => {
    // State for the 'Add Keyword' form
    const [newKeyword, setNewKeyword] = useState('');
    const [newFacebookPageUrl, setNewFacebookPageUrl] = useState('');
    const [newSelectedPlatforms, setNewSelectedPlatforms] = useState({ twitter: false, youtube: false, facebook: false, instagram: false, linkedin: false, tiktok: false });

    // State for filtering the mentions table
    const [selectedKeywordId, setSelectedKeywordId] = useState(null);
    const [isPageFetching, setIsPageFetching] = useState(null);

    // --- NEW: State for the Edit Keyword Dialog ---
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingKeyword, setEditingKeyword] = useState(null);
    const [editFormState, setEditFormState] = useState({ term: '', facebookPageUrl: '', platforms: {} });

    // --- Keyword Actions ---
    const handleAddKeyword = async (e) => {
        e.preventDefault();
        const platforms = Object.keys(newSelectedPlatforms).filter(p => newSelectedPlatforms[p]);
        if (!newKeyword || platforms.length === 0) return alert('Keyword term and at least one platform are required.');
        try {
            await api.post(`/keywords`, {
                term: newKeyword,
                platforms,
                facebookPageUrl: newSelectedPlatforms.facebook ? newFacebookPageUrl : null
            });
            setNewKeyword('');
            setNewFacebookPageUrl('');
            setNewSelectedPlatforms({ twitter: false, youtube: false, facebook: false, instagram: false, linkedin: false, tiktok: false });
            await refreshData();
        } catch (err) {
            alert('Failed to add keyword.');
        }
    };

    const handleDeleteKeyword = async (id) => {
        if (window.confirm('Are you sure you want to delete this keyword and all its associated mentions?')) {
            try {
                await api.delete(`/keywords/${id}`);
                if (selectedKeywordId === id) setSelectedKeywordId(null);
                await refreshData();
            } catch (err) {
                alert('Failed to delete keyword.');
            }
        }
    };

    const handleFetchKeywordPage = async (keywordId) => {
        setIsPageFetching(keywordId);
        try {
            const response = await api.post(`/meta/fetch-keyword-page/${keywordId}`);
            if (response.data.length > 0) {
                await api.post(`/mentions/batch`, response.data.map(m => ({ ...m, keywordId })));
                alert(`Found and saved ${response.data.length} new mentions. Refreshing data...`);
                await refreshData();
            } else {
                alert('No new mentions found on the page matching your keyword.');
            }
        } catch (err) {
            alert('Failed to fetch from the specified page/group.');
        } finally {
            setIsPageFetching(null);
        }
    };

    // --- Edit Dialog Actions ---
    const handleOpenEditDialog = (keyword) => {
        setEditingKeyword(keyword);
        const platformsState = ['X', 'youtube', 'facebook', 'instagram', 'linkedin', 'tiktok'].reduce((acc, p) => {
            acc[p] = (keyword.platforms || []).includes(p);
            return acc;
        }, {});
        setEditFormState({
            term: keyword.term,
            facebookPageUrl: keyword.facebookPageUrl || '',
            platforms: platformsState
        });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingKeyword(null);
    };

    const handleEditFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setEditFormState(prev => ({
                ...prev,
                platforms: { ...prev.platforms, [name]: checked }
            }));
        } else {
            setEditFormState(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleUpdateKeyword = async () => {
        const platforms = Object.keys(editFormState.platforms).filter(p => editFormState.platforms[p]);
        if (!editFormState.term || platforms.length === 0) return alert('Keyword term and platform are required.');

        try {
            await api.put(`/keywords/${editingKeyword.id}`, {
                term: editFormState.term,
                platforms,
                facebookPageUrl: editFormState.platforms.facebook ? editFormState.facebookPageUrl : null
            });
            handleCloseEditDialog();
            await refreshData();
        } catch (error) {
            alert('Failed to update keyword.');
        }
    };

    // Filter mentions based on the selected keyword
    const filteredMentions = selectedKeywordId
        ? state.mentions.filter(m => m.keywordId === selectedKeywordId)
        : state.mentions;

    return (
        <Container maxWidth="xl">
            <Typography variant="h4" gutterBottom>Keywords & Mentions</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Manage Keywords</Typography>
                                <Box component="form" onSubmit={handleAddKeyword}>
                                    <TextField fullWidth label="New Keyword" value={newKeyword} onChange={e => setNewKeyword(e.target.value)} sx={{ mb: 2 }} />
                                    <FormGroup>
                                        <FormControlLabel control={<Checkbox checked={newSelectedPlatforms.twitter} onChange={e => setNewSelectedPlatforms({ ...newSelectedPlatforms, twitter: e.target.checked })} />} label="X" />
                                        <FormControlLabel control={<Checkbox checked={newSelectedPlatforms.youtube} onChange={e => setNewSelectedPlatforms({ ...newSelectedPlatforms, youtube: e.target.checked })} />} label="YouTube" />
                                        <FormControlLabel control={<Checkbox checked={newSelectedPlatforms.facebook} onChange={e => setNewSelectedPlatforms({ ...newSelectedPlatforms, facebook: e.target.checked })} />} label="Facebook" />
                                        <FormControlLabel control={<Checkbox checked={newSelectedPlatforms.instagram} onChange={e => setNewSelectedPlatforms({ ...newSelectedPlatforms, instagram: e.target.checked })} />} label="Instagram" />
                                        <FormControlLabel control={<Checkbox checked={newSelectedPlatforms.linkedin} onChange={e => setNewSelectedPlatforms({ ...newSelectedPlatforms, linkedin: e.target.checked })} />} label="LinkedIn" />
                                        <FormControlLabel control={<Checkbox checked={newSelectedPlatforms.tiktok} onChange={e => setNewSelectedPlatforms({ ...newSelectedPlatforms, tiktok: e.target.checked })} />} label="TikTok" />
                                    </FormGroup>
                                    {newSelectedPlatforms.facebook && (
                                        <TextField fullWidth label="Facebook Page/Group URL (Optional)" value={newFacebookPageUrl} onChange={e => setNewFacebookPageUrl(e.target.value)} sx={{ mt: 2 }} helperText="For scraping a specific page/group" />
                                    )}
                                    <Button type="submit" variant="contained" fullWidth sx={{ mt: 2,backgroundColor: 'black','&:hover': {      backgroundColor: '#333'} }}>Add Keyword</Button>
                                   
                                </Box>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Your Keywords</Typography>
                                <List>
                                    <ListItemButton onClick={() => setSelectedKeywordId(null)} selected={selectedKeywordId === null}>
                                        <ListItemText primary="Show All Mentions" />
                                    </ListItemButton>
                                    {state.keywords.map(kw => (
                                        <ListItem key={kw.id} secondaryAction={
                                            <Stack direction="row" spacing={0.5}>
                                                {kw.facebookPageUrl && (
                                                    <Button size="small" sx={{minWidth: 'auto', px: 1}} variant="outlined" onClick={() => handleFetchKeywordPage(kw.id)} disabled={isPageFetching === kw.id}>
                                                        {isPageFetching === kw.id ? '...' : 'Fetch'}
                                                    </Button>
                                                )}
                                                <IconButton onClick={() => handleOpenEditDialog(kw)}><EditIcon /></IconButton>
                                                <IconButton onClick={() => handleDeleteKeyword(kw.id)}><DeleteIcon /></IconButton>
                                            </Stack>
                                        }>
                                            <ListItemButton onClick={() => setSelectedKeywordId(kw.id)} selected={selectedKeywordId === kw.id}>
                                                <ListItemText primary={kw.term} secondary={(kw.platforms || []).join(', ')} />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Mentions for "{selectedKeywordId ? state.keywords.find(k => k.id === selectedKeywordId)?.term : 'All Keywords'}"</Typography>
                            <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Platform</TableCell>
                                            <TableCell>Author</TableCell>
                                            <TableCell>Content</TableCell>
                                            <TableCell>Sentiment</TableCell>
                                            <TableCell>Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredMentions.map(m => (
                                            <TableRow key={m.id || m.postUrl}>
                                                <TableCell>{m.platform}</TableCell>
                                                <TableCell>{getAuthorName(m.author)}</TableCell>
                                                <TableCell>{getContentText(m.content).substring(0, 50)}...</TableCell>
                                                <TableCell>{m.sentiment}</TableCell>
                                                <TableCell>{new Date(m.publishedAt).toLocaleDateString()}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Edit Keyword Dialog */}
            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} fullWidth maxWidth="sm">
                <DialogTitle>Edit Keyword</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ pt: 1 }}>
                        <TextField
                            name="term"
                            label="Keyword Term"
                            fullWidth
                            value={editFormState.term}
                            onChange={handleEditFormChange}
                            sx={{ mb: 2 }}
                        />
                        <FormGroup>
                            <FormControlLabel control={<Checkbox name="twitter" checked={editFormState.platforms.twitter || false} onChange={handleEditFormChange} />} label="X" />
                            <FormControlLabel control={<Checkbox name="youtube" checked={editFormState.platforms.youtube || false} onChange={handleEditFormChange} />} label="YouTube" />
                            <FormControlLabel control={<Checkbox name="facebook" checked={editFormState.platforms.facebook || false} onChange={handleEditFormChange} />} label="Facebook" />
                            <FormControlLabel control={<Checkbox name="instagram" checked={editFormState.platforms.instagram || false} onChange={handleEditFormChange} />} label="Instagram" />
                            <FormControlLabel control={<Checkbox name="linkedin" checked={editFormState.platforms.linkedin || false} onChange={handleEditFormChange} />} label="LinkedIn" />
                            <FormControlLabel control={<Checkbox name="tiktok" checked={editFormState.platforms.tiktok || false} onChange={handleEditFormChange} />} label="TikTok" />
                        </FormGroup>
                        {editFormState.platforms.facebook && (
                            <TextField
                                name="facebookPageUrl"
                                label="Facebook Page/Group URL"
                                fullWidth
                                value={editFormState.facebookPageUrl}
                                onChange={handleEditFormChange}
                                sx={{ mt: 2 }}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog}>Cancel</Button>
                    <Button onClick={handleUpdateKeyword} variant="contained">Save Changes</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default KeywordsPage;

